import { MusePeer } from '@/services/RoomContextMusePeer'
import Tooltip from '@/UIComponents2/Tooltip'
import { UserContextState, useUserContext } from '@/UserContext'
import { useTranslation } from 'react-i18next'

type UsernameProps = {
    // Directly pass the names
    username?: string
    displayName?: string
    isGuest?: boolean

    // OR, set isMe and the component will lookup the username from userState
    isMe?: boolean

    // OR, pass in a MusePeer and the comonent will use it to render the username
    peer?: MusePeer

    // Render the '(You)' or '(Guest)' suffix as appropriate
    suffix?: boolean

    // If the username is longer than maxLength, it will be truncated and
    // wrapped in a tooltip to show the full name
    maxLength?: number
    // Suppress the tooltip for long names
    noTooltip?: boolean

    className?: string
}

export function Username(props: UsernameProps) {
    const { t } = useTranslation()
    const [userState] = useUserContext()

    const maxLength = props.maxLength ?? 15
    const name = getName(props, userState) ?? 'unknown'

    let suffix = ''
    if (props.suffix) {
        if (props.isMe) {
            suffix = t('You')
        } else if (props.isGuest || props.peer?.isGuest) {
            suffix = t('Guest')
        }
    }

    if (name.length > maxLength) {
        return (
            <Tooltip text={!props.noTooltip && name}>
                <span className={props.className}>
                    {name.slice(0, maxLength)}…{suffix && ` (${suffix})`}
                </span>
            </Tooltip>
        )
    }

    return (
        <span className={props.className}>
            {name}
            {suffix && ` (${suffix})`}
        </span>
    )
}

function getName(opts: UsernameProps, user?: UserContextState): string | undefined {
    if (opts.peer) {
        if (opts.peer.isGuest) {
            return opts.peer.displayName ?? opts.displayName ?? opts.peer.username
        }
        return opts.username ?? opts.peer.username
    }

    if (opts.isMe) {
        if (opts.isGuest || user?.isGuest) {
            return opts.displayName ?? user?.profile?.displayName ?? opts.username
        }
        return opts.username ?? user?.profile?.username
    }

    if (opts.isGuest) {
        return opts.displayName ?? opts.username
    }

    return opts.username
}
